/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/destructuring-assignment */
import {
  ActionIcon,
  Grid,
  Group,
  Input,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Space,
  Stack,
  Switch,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconBrightnessHalf, IconClock, IconInfoCircle, IconNotes } from '@tabler/icons-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import BasicPricing, {
  BasicPricingData,
  BasicPricingRef,
} from '../../../../../../../../components/BasicPricing';
import BasicValueUnits, {
  BasicValueUnitsData,
  BasicValueUnitsRef,
} from '../../../../../../../../components/BasicValueUnits';
import EntityAddressPicker from '../../../../../../../../components/core/EntityAddressPicker/EntityAddressPicker';
import EntityPicker from '../../../../../../../../components/core/EntityPicker/EntityPicker';
import EquipmentPicker from '../../../../../../../../components/core/EquipmentPicker/EquipmentPicker';
import PackagingPicker from '../../../../../../../../components/core/PackagingPicker/PackagingPicker';
import PageSection from '../../../../../../../../components/core/PageSection/PageSection';
import ServicePicker from '../../../../../../../../components/core/ServicePicker/ServicePicker';
import { ProposalGeneralData } from '../../../../../../../../contexts/core/proposals/ProposalGeneral.context';
import { useProposalServiceContext } from '../../../../../../../../contexts/core/proposals/ProposalService.context';
import { EntityTypeType, UnitOfMeasureType } from '../../../../../../../../models/core/cache.type';
import { Action } from '../../../../../../../../models/core/core.type';
import {
  EntityAddressType,
  EntitySearchResponseType,
} from '../../../../../../../../models/core/entities.type';
import { EquipmentType } from '../../../../../../../../models/core/equipment.type';
import { PackagingType } from '../../../../../../../../models/core/packaging.type';
import {
  ProposalResiduePlanPricingType,
  ProposalServiceType,
  ProposalTipoPrecificacaoEnum,
} from '../../../../../../../../models/core/proposals.type';
import { ServiceType } from '../../../../../../../../models/core/services.type';
import theme from '../../../../../../../../theme';
import { EntityItemEnum, Feature, Financial } from '../../../../../../../../utils/constants.utils';
import { buildFakeAuditObject, newGuid } from '../../../../../../../../utils/helper.utils';

type FormViewData = {
  action: Action;
  id: string;
  idServico: number | null;
  servico: string;
  tipo: string;
  quantidade: number | null;
  precificacao: BasicPricingData;
  idResiduoAcondicionamento: number | null;
  residuoAcondicionamento: string;
  idResiduoEquipamento: number | null;
  residuoEquipamento: string;
  frequencia: number | null;
  frequenciaIdUnidadeMedida1: string | null;
  frequenciaIdUnidadeMedida2: string | null;
  cobrarTolerancia: boolean;
  compartilhado: boolean;
  gerenciar: boolean;
  turno: string | null;
  jornada: string | null;
  clienteData: EntitySearchResponseType | null;
  idCliente: number | null;
  cliente: string;
  enderecoData: EntityAddressType | null;
  idEntidadeEndereco: number | null;
  endereco: string;
  observacao: string | null;
};

type ProposalFormViewProps = {
  referenceData: {
    unitOfMeasures: UnitOfMeasureType[];
    entityTypeData: EntityTypeType[];
  };
  proposalGeneralData: ProposalGeneralData;
  origItem: ProposalServiceType | null;
  item: ProposalServiceType | null;
  idProposta: number | undefined;
  callback(item: ProposalServiceType | null, action: string, confirmed: boolean): void;
  disabled: boolean;
  zIndex: number;
};

const ServiceGeneralFormView = forwardRef((props: ProposalFormViewProps, ref) => {
  const { setProposalServiceData } = useProposalServiceContext();
  const refBasicPricing = useRef<BasicPricingRef>(null);
  const refFrequency = useRef<BasicValueUnitsRef>(null);

  const buildPricingFormData = (item: ProposalServiceType | null): BasicPricingData => ({
    tipoPrecificacao: item?.precificacao?.tipo || ProposalTipoPrecificacaoEnum.Geral,
    quantidade: item?.precificacao?.quantidade ?? 1,
    quantidadeIdUnidadeMedida:
      item?.precificacao?.quantidadeIdUnidadeMedida?.toString() ||
      props.referenceData.unitOfMeasures
        .find((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
        ?.idUnidadeMedida?.toString() ||
      null,
    frequenciaIdUnidadeMedida: item?.precificacao?.frequenciaIdUnidadeMedida?.toString() || null,
    preco: item?.precificacao?.preco ?? null,
    imposto: item?.precificacao?.imposto ?? Financial.Tax,
  });

  const buildPricingType = useCallback(
    (pricingData: BasicPricingData): ProposalResiduePlanPricingType => {
      const quantidadeIdUnidadeMedida = pricingData.quantidadeIdUnidadeMedida
        ? Number(pricingData.quantidadeIdUnidadeMedida)
        : null;
      const frequenciaIdUnidadeMedida = pricingData.frequenciaIdUnidadeMedida
        ? Number(pricingData.frequenciaIdUnidadeMedida)
        : null;

      return {
        tipo: pricingData.tipoPrecificacao,
        quantidade: pricingData.quantidade === null ? null : Number(pricingData.quantidade),
        quantidadeIdUnidadeMedida,
        quantidadeUnidadeMedida:
          props.referenceData.unitOfMeasures.find((x) => x.idUnidadeMedida === quantidadeIdUnidadeMedida)
            ?.unidadeMedida || null,
        quantidadeUnidadeMedidaSigla:
          props.referenceData.unitOfMeasures.find((x) => x.idUnidadeMedida === quantidadeIdUnidadeMedida)
            ?.unidadeMedidaSigla || null,
        frequenciaIdUnidadeMedida,
        frequenciaUnidadeMedida:
          props.referenceData.unitOfMeasures.find((x) => x.idUnidadeMedida === frequenciaIdUnidadeMedida)
            ?.unidadeMedida || null,
        frequenciaUnidadeMedidaSigla:
          props.referenceData.unitOfMeasures.find((x) => x.idUnidadeMedida === frequenciaIdUnidadeMedida)
            ?.unidadeMedidaSigla || null,
        preco: pricingData.preco === null ? null : Number(pricingData.preco),
        imposto: pricingData.imposto === null ? null : Number(pricingData.imposto),
      };
    },
    [props.referenceData.unitOfMeasures]
  );

  const frequencyUnit1Options = props.referenceData.unitOfMeasures
    .filter(
      (x) =>
        (x.tipo === 'Tempo' && x.unidadeMedidaSigla === 'h') ||
        (x.tipo === 'Outro' && x.unidadeMedidaSigla !== 'per')
    )
    .map((x) => ({
      value: x.idUnidadeMedida.toString(),
      label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
      group: x.tipo,
    }));

  const frequencyUnit2Options = props.referenceData.unitOfMeasures
    .filter(
      (x) =>
        (x.tipo === 'Tempo' &&
          (x.unidadeMedidaSigla === 'd' ||
            x.unidadeMedidaSigla === 'sem' ||
            x.unidadeMedidaSigla === 'mês' ||
            x.unidadeMedidaSigla === 'ano')) ||
        (x.tipo === 'Outro' && x.unidadeMedidaSigla === 'per')
    )
    .map((x) => ({
      value: x.idUnidadeMedida.toString(),
      label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
      group: x.tipo,
    }));

  const buildClienteDataFromItem = (item: ProposalServiceType | null): EntitySearchResponseType | null => {
    if (!item) {
      return null;
    }
    return {
      ...buildFakeAuditObject(),
      idEntidade: Number(item.idCliente),
      tipos: [],
      cnpj: item.clienteCNPJ || null,
      razaoSocial: item.clienteRazaoSocial || null,
      nomeFantasia: item.clienteNomeFantasia || null,
      incricaoEstadual: null,
      incricaoMunicipal: null,
      cpf: item.clienteCPF || null,
      nome: item.clienteNome || null,
      webSite: null,
      aceitaEncontroContas: false,
    };
  };

  const buildEnderecoDataFromItem = (item: ProposalServiceType | null): EntityAddressType | null => {
    if (!item) {
      return null;
    }
    return {
      ...buildFakeAuditObject(),
      idEntidadeEndereco: item.idEntidadeEndereco,
      idEntidade: item.idCliente,
      codigoEnderecoTipo: '',
      enderecoTipo: '',
      codigoEstado: item.codigoEstado,
      estado: '',
      cidade: item.cidade,
      cidadeCodigoIBGE: item.cidadeCodigoIBGE || null,
      bairro: item.bairro,
      logradouro: item.logradouro,
      numero: item.numero,
      complemento: item.complemento || null,
      referencia: item.referencia || null,
      cep: item.cep,
      latitude: item.latitude || null,
      longitude: item.longitude || null,
      contatos: [],
    };
  };

  const form = useForm<FormViewData>({
    initialValues: {
      action: props.origItem ? Action.Nothing : Action.Add,
      id: props.item?.id || newGuid(),
      idServico: props.item?.idServico || null,
      servico: props.item?.servico || '',
      tipo: props.item?.idResiduoAcondicionamento
        ? EntityItemEnum.Packaging
        : props.item?.idResiduoEquipamento
        ? EntityItemEnum.Equipment
        : EntityItemEnum.Service,
      quantidade: props.item?.quantidade || 1,
      precificacao: buildPricingFormData(props.item),
      idResiduoAcondicionamento: props.item?.idResiduoAcondicionamento || null,
      residuoAcondicionamento: props.item?.residuoAcondicionamento || '',
      idResiduoEquipamento: props.item?.idResiduoEquipamento || null,
      residuoEquipamento: props.item?.residuoEquipamento || '',
      frequencia: props.item?.frequencia || 1,
      frequenciaIdUnidadeMedida1: props.item?.frequenciaIdUnidadeMedida1?.toString() || null,
      frequenciaIdUnidadeMedida2: props.item?.frequenciaIdUnidadeMedida2?.toString() || null,
      cobrarTolerancia: props.item ? props.item.cobrarTolerancia : false,
      compartilhado: props.item ? props.item.compartilhado : false,
      gerenciar: props.item ? props.item.gerenciar : true,
      turno: props.item?.turno?.toString() || '',
      jornada: props.item?.jornada || '',
      clienteData: buildClienteDataFromItem(props.item) || props.proposalGeneralData.cliente,
      idCliente: props.item?.idCliente || props.proposalGeneralData.cliente?.idEntidade || null,
      cliente:
        props.item?.clienteNomeFantasia ||
        props.item?.clienteRazaoSocial ||
        props.item?.clienteNome ||
        props.proposalGeneralData.cliente?.nomeFantasia ||
        props.proposalGeneralData.cliente?.razaoSocial ||
        props.proposalGeneralData.cliente?.nome ||
        '',
      enderecoData: buildEnderecoDataFromItem(props.item),
      idEntidadeEndereco: props.item?.idEntidadeEndereco || null,
      endereco: props.item?.idEntidadeEndereco
        ? `${props.item.logradouro}, ${props.item.numero} | ${props.item.bairro} - ${props.item.cidade}/${props.item.codigoEstado}`
        : '',
      observacao: props.item?.observacao || '',
    },
    validate: {
      idServico: (value) => (!value || value <= 0 ? 'Campo obrigatório' : null),
      quantidade: (value: number | null) => (!value ? 'Campo obrigatório' : null),
      idResiduoAcondicionamento: (value, values: FormViewData) =>
        (!value || value <= 0) && values.tipo === EntityItemEnum.Packaging ? 'Campo obrigatório' : null,
      idResiduoEquipamento: (value, values: FormViewData) =>
        (!value || value <= 0) && values.tipo === EntityItemEnum.Equipment ? 'Campo obrigatório' : null,
      idCliente: (value) => (!value || value <= 0 ? 'Campo obrigatório' : null),
      idEntidadeEndereco: (value) => (!value || value <= 0 ? 'Campo obrigatório' : null),
    },
  });

  const isModified = (pricingData?: BasicPricingData, frequencyData?: BasicValueUnitsData): boolean => {
    const origData = `${props.origItem?.idServico || ''}
    |${props.origItem?.quantidade || ''}
    |${props.origItem?.idResiduoAcondicionamento || ''}
    |${props.origItem?.idResiduoEquipamento || ''}
    |${props.origItem?.precificacao?.quantidade || ''}
    |${props.origItem?.precificacao?.quantidadeIdUnidadeMedida || ''}
    |${props.origItem?.precificacao?.frequenciaIdUnidadeMedida || ''}
    |${props.origItem?.precificacao?.tipo || ''}
    |${Number(props.origItem?.precificacao?.preco) || ''}
    |${Number(props.origItem?.precificacao?.imposto) || ''}
    |${Number(props.origItem?.frequencia) || ''}
    |${props.origItem?.frequenciaIdUnidadeMedida1 || ''}
    |${props.origItem?.frequenciaIdUnidadeMedida2 || ''}
    |${props.origItem?.cobrarTolerancia || ''}
    |${props.origItem?.compartilhado || ''}
    |${props.origItem?.gerenciar || ''}
    |${Number(props.origItem?.turno) || ''}
    |${props.origItem?.jornada || ''}
    |${props.origItem?.idCliente || ''}
    |${props.origItem?.idEntidadeEndereco || ''}
    |${props.origItem?.observacao || ''}`;

    const formData = `${form.values.idServico || ''}
    |${form.values.quantidade || ''}
    |${form.values.idResiduoAcondicionamento || ''}
    |${form.values.idResiduoEquipamento || ''}
    |${pricingData?.quantidade ?? (form.values.precificacao.quantidade || '')}
    |${pricingData?.quantidadeIdUnidadeMedida ?? (form.values.precificacao.quantidadeIdUnidadeMedida || '')}
    |${pricingData?.frequenciaIdUnidadeMedida ?? (form.values.precificacao.frequenciaIdUnidadeMedida || '')}
    |${pricingData?.tipoPrecificacao ?? form.values.precificacao.tipoPrecificacao}
    |${Number(pricingData?.preco ?? form.values.precificacao.preco) || ''}
    |${Number(pricingData?.imposto ?? form.values.precificacao.imposto) || ''}
    |${Number(frequencyData?.valor ?? form.values.frequencia) || ''}
    |${frequencyData?.idUnidade1 ?? (form.values.frequenciaIdUnidadeMedida1 || '')}
    |${frequencyData?.idUnidade2 ?? (form.values.frequenciaIdUnidadeMedida2 || '')}
    |${form.values.cobrarTolerancia || ''}
    |${form.values.compartilhado || ''}
    |${form.values.gerenciar || ''}
    |${Number(form.values.turno) || ''}
    |${form.values.jornada || ''}
    |${form.values.idCliente || ''}
    |${form.values.idEntidadeEndereco || ''}
    |${form.values.observacao || ''}`;

    return origData !== formData;
  };

  useImperativeHandle(ref, () => ({
    validate(): ProposalServiceType | null {
      const pricingData = refBasicPricing.current?.validate();
      const frequencyData =
        form.values.tipo === EntityItemEnum.Service ? refFrequency.current?.validate() : null;
      if (
        form.validate().hasErrors ||
        !pricingData ||
        (form.values.tipo === EntityItemEnum.Service && !frequencyData)
      ) {
        throw Error('Existem pendências a serem corrigidas.');
      }
      let formValues = form.values;

      const precificacao = buildPricingType(pricingData);
      if (props.origItem) {
        if (isModified(pricingData, frequencyData || undefined)) {
          formValues.action = Action.Modify;
        } else {
          formValues = {
            ...formValues,
            ...props.origItem,
            action: Action.Nothing,
            residuoAcondicionamento: form.values.residuoAcondicionamento || '',
            residuoEquipamento: form.values.residuoEquipamento || '',
            frequenciaIdUnidadeMedida1:
              frequencyData?.idUnidade1 || form.values.frequenciaIdUnidadeMedida1 || '',
            frequenciaIdUnidadeMedida2:
              frequencyData?.idUnidade2 || form.values.frequenciaIdUnidadeMedida2 || '',
            precificacao: pricingData,
          };
        }
      }

      const formItem: ProposalServiceType = {
        ...formValues,
        ...buildFakeAuditObject(),
        idServico: Number(formValues.idServico),
        servico: formValues.servico,
        quantidade: Number(formValues.quantidade),
        precificacao,
        idCliente: Number(formValues.idCliente),
        clienteCNPJ: formValues.clienteData?.cnpj,
        clienteRazaoSocial: formValues.clienteData?.razaoSocial,
        clienteNomeFantasia: formValues.clienteData?.nomeFantasia,
        clienteCPF: formValues.clienteData?.cpf,
        clienteNome: formValues.clienteData?.nome,
        idEntidadeEndereco: Number(formValues.idEntidadeEndereco),
        codigoEstado: formValues.enderecoData?.codigoEstado,
        cidade: formValues.enderecoData?.cidade,
        cidadeCodigoIBGE: formValues.enderecoData?.cidadeCodigoIBGE,
        bairro: formValues.enderecoData?.bairro,
        logradouro: formValues.enderecoData?.logradouro,
        numero: formValues.enderecoData?.numero,
        complemento: formValues.enderecoData?.complemento,
        referencia: formValues.enderecoData?.referencia,
        cep: formValues.enderecoData?.cep,
        latitude: formValues.enderecoData?.latitude,
        longitude: formValues.enderecoData?.longitude,
        frequencia: Number(frequencyData?.valor ?? formValues.frequencia),
        frequenciaIdUnidadeMedida1: Number(
          frequencyData?.idUnidade1 ?? formValues.frequenciaIdUnidadeMedida1
        ),
        frequenciaIdUnidadeMedida2: Number(
          frequencyData?.idUnidade2 ?? formValues.frequenciaIdUnidadeMedida2
        ),
        idProposta: props.idProposta || -1,
      };

      if (formValues.tipo === EntityItemEnum.Packaging) {
        formItem.idResiduoEquipamento = null;
        formItem.residuoEquipamento = null;
        formItem.residuoEquipamentoDescricao = null;
      } else if (formValues.tipo === EntityItemEnum.Equipment) {
        formItem.idResiduoAcondicionamento = null;
        formItem.residuoAcondicionamento = null;
        formItem.residuoAcondicionamentoDescricao = null;
        formItem.compartilhado = false;
        formItem.gerenciar = true;
      } else {
        formItem.idResiduoEquipamento = null;
        formItem.residuoEquipamento = null;
        formItem.residuoEquipamentoDescricao = null;
        formItem.idResiduoAcondicionamento = null;
        formItem.residuoAcondicionamento = null;
        formItem.residuoAcondicionamentoDescricao = null;
        formItem.compartilhado = false;
        formItem.gerenciar = true;
      }

      if (formValues.tipo !== EntityItemEnum.Service) {
        formItem.frequencia = 1;
        formItem.frequenciaIdUnidadeMedida1 = Number(
          props.referenceData.unitOfMeasures.find((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
            ?.idUnidadeMedida || null
        );
        formItem.frequenciaIdUnidadeMedida2 = Number(
          props.referenceData.unitOfMeasures.find((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'per')
            ?.idUnidadeMedida || null
        );
        formItem.turno = null;
        formItem.jornada = null;
      }

      formItem.frequenciaUnidadeMedida1 = props.referenceData.unitOfMeasures.find(
        (x) => x.idUnidadeMedida === formItem.frequenciaIdUnidadeMedida1
      )?.unidadeMedida;
      formItem.frequenciaUnidadeMedida2 = props.referenceData.unitOfMeasures.find(
        (x) => x.idUnidadeMedida === formItem.frequenciaIdUnidadeMedida2
      )?.unidadeMedida;
      formItem.jornada = formItem.jornada?.trim() || null;
      formItem.observacao = formItem.observacao?.trim() || null;

      return formItem;
    },
    getFormItemValue(item: keyof FormViewData) {
      return form.getInputProps(item).value;
    },
    clear() {
      form.reset();
      form.setFieldValue('idCliente', -1);
      form.setFieldValue('cliente', '');
      form.setFieldValue('idEntidadeEndereco', -1);
      form.setFieldValue('endereco', '');
    },
  }));

  useEffect(() => {
    setProposalServiceData({
      tipo: form.values.tipo as EntityItemEnum,
      servico: form.values.idServico ? { ...form.values, ...buildFakeAuditObject(), descricao: null } : null,
      quantidade: form.values.quantidade,
      precificacao: buildPricingType(form.values.precificacao),
      acondicionamento: form.values.idResiduoAcondicionamento
        ? { ...form.values, ...buildFakeAuditObject(), descricao: null }
        : null,
      equipamento: form.values.idResiduoEquipamento
        ? { ...form.values, ...buildFakeAuditObject(), descricao: null }
        : null,
      frequencia: form.values.frequencia,
      frequenciaIdUnidadeMedida1: Number(form.values.frequenciaIdUnidadeMedida1),
      frequenciaIdUnidadeMedida2: Number(form.values.frequenciaIdUnidadeMedida2),
      idEntidadeEndereco: form.values.idEntidadeEndereco,
    });
  }, [buildPricingType, form.values, setProposalServiceData]);

  return (
    <div>
      <form id="proposal-service" noValidate>
        <Paper shadow="xs" p="md" withBorder>
          <PageSection
            size="lg"
            color={Feature.Home.Proposal.color}
            label="Geral"
            text="Informações gerais do serviço como referências, local e dados operacionais."
          />
          <Space h="xs" />

          <Grid columns={6}>
            <Grid.Col span={2}>
              <ServicePicker
                label="Serviço"
                formItem={form.getInputProps('idServico')}
                idsToBeDisabled={[]}
                callback={(item: ServiceType | null) => {
                  if (item) {
                    form.setFieldValue('idServico', item.idServico);
                    form.setFieldValue('servico', item.servico);
                  }
                }}
                disabled={props.disabled}
                zIndex={props.zIndex + 1}
                useCache
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <NumberInput
                label="Quantidade"
                placeholder="Quantidade"
                min={1}
                defaultValue={1}
                step={1}
                stepHoldDelay={500}
                stepHoldInterval={(t) => Math.max(1000 / t ** 2, 25)}
                required
                {...form.getInputProps('quantidade')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <div>
                <Input.Wrapper label="Referência" required>
                  <Group
                    style={{ display: 'inline-block', marginLeft: 5, marginTop: -2, verticalAlign: 'bottom' }}
                  >
                    <Tooltip
                      withArrow
                      transitionProps={{ transition: 'fade', duration: 200 }}
                      label="Determina o tipo do item a ser associado com este serviço."
                      zIndex={props.zIndex + 1}
                    >
                      <div>
                        <IconInfoCircle size={18} color={theme?.colors?.accent?.[6]} />
                      </div>
                    </Tooltip>
                  </Group>
                </Input.Wrapper>
                <Select
                  placeholder="Selecione..."
                  data={[
                    { label: 'Fornecedor', value: EntityItemEnum.Service },
                    { label: 'Acondicionamento', value: EntityItemEnum.Packaging },
                    { label: 'Equipamento', value: EntityItemEnum.Equipment },
                  ]}
                  value={form.values.tipo}
                  onChange={(tipo: string | any) => {
                    form.setFieldValue('tipo', tipo);
                    if (tipo === EntityItemEnum.Packaging || tipo === EntityItemEnum.Equipment) {
                      form.setFieldValue('frequencia', 1);
                      form.setFieldValue(
                        'frequenciaIdUnidadeMedida1',
                        props.referenceData.unitOfMeasures
                          .find((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
                          ?.idUnidadeMedida?.toString() || null
                      );
                      form.setFieldValue(
                        'frequenciaIdUnidadeMedida2',
                        props.referenceData.unitOfMeasures
                          .find((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'per')
                          ?.idUnidadeMedida?.toString() || null
                      );
                      form.setFieldValue('turno', null);
                      form.setFieldValue('jornada', null);
                    } else {
                      form.setFieldValue('frequencia', 1);
                      form.setFieldValue('frequenciaIdUnidadeMedida1', null);
                      form.setFieldValue('frequenciaIdUnidadeMedida2', null);
                    }
                    if (tipo === EntityItemEnum.Packaging) {
                      form.setFieldValue('idResiduoEquipamento', null);
                      form.setFieldValue('residuoEquipamento', '');
                    } else if (tipo === EntityItemEnum.Equipment) {
                      form.setFieldValue('idResiduoAcondicionamento', null);
                      form.setFieldValue('residuoAcondicionamento', '');
                    } else {
                      form.setFieldValue('idResiduoEquipamento', null);
                      form.setFieldValue('residuoEquipamento', '');
                      form.setFieldValue('idResiduoAcondicionamento', null);
                      form.setFieldValue('residuoAcondicionamento', '');
                    }
                  }}
                  disabled={props.disabled}
                  required
                  comboboxProps={{ zIndex: props.zIndex + 1 }}
                />
              </div>
            </Grid.Col>
            {form.values.tipo === EntityItemEnum.Packaging ? (
              <>
                <Grid.Col span={1}>
                  <PackagingPicker
                    label="Acondicionamento"
                    formItem={form.getInputProps('idResiduoAcondicionamento')}
                    idsToBeDisabled={[]}
                    callback={(item: PackagingType | null) => {
                      if (item) {
                        form.setFieldValue('idResiduoAcondicionamento', item.idResiduoAcondicionamento);
                        form.setFieldValue('residuoAcondicionamento', item.residuoAcondicionamento);
                      }
                    }}
                    disabled={props.disabled}
                    zIndex={props.zIndex + 1}
                    useCache
                  />
                </Grid.Col>
                <Grid.Col span={1}>
                  <Stack gap="xs">
                    <Switch
                      label="Gerenciar?"
                      disabled={props.disabled}
                      {...form.getInputProps('gerenciar', { type: 'checkbox' })}
                    />
                    <Switch
                      label="Compartilhado?"
                      disabled={props.disabled}
                      {...form.getInputProps('compartilhado', { type: 'checkbox' })}
                    />
                  </Stack>
                </Grid.Col>
              </>
            ) : form.values.tipo === EntityItemEnum.Equipment ? (
              <Grid.Col span={2}>
                <EquipmentPicker
                  label="Equipamento"
                  formItem={form.getInputProps('idResiduoEquipamento')}
                  idsToBeDisabled={[]}
                  callback={(item: EquipmentType | null) => {
                    if (item) {
                      form.setFieldValue('idResiduoEquipamento', item.idResiduoEquipamento);
                      form.setFieldValue('residuoEquipamento', item.residuoEquipamento);
                    }
                  }}
                  disabled={props.disabled}
                  zIndex={props.zIndex + 1}
                  useCache
                />
              </Grid.Col>
            ) : (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <></>
            )}
          </Grid>
          <Space h="xs" />

          {form.values.tipo === EntityItemEnum.Service && (
            <>
              <Grid columns={6}>
                <Grid.Col span={3}>
                  <BasicValueUnits
                    ref={refFrequency}
                    isEdit
                    disabled={props.disabled}
                    zIndex={props.zIndex + 1}
                    label="Frequência"
                    placeholderValor="Frequência"
                    validationMode="all"
                    valueMin={0.01}
                    valueDefaultValue={1}
                    valueMustBePositiveWhenFilled
                    value={{
                      valor: form.values.frequencia,
                      idUnidade1: form.values.frequenciaIdUnidadeMedida1,
                      idUnidade2: form.values.frequenciaIdUnidadeMedida2,
                    }}
                    onChange={(frequencyData) => {
                      form.setFieldValue('frequencia', frequencyData.valor);
                      form.setFieldValue('frequenciaIdUnidadeMedida1', frequencyData.idUnidade1);
                      form.setFieldValue('frequenciaIdUnidadeMedida2', frequencyData.idUnidade2);
                    }}
                    unit1Options={frequencyUnit1Options}
                    unit2Options={frequencyUnit2Options}
                  />
                </Grid.Col>

                <Grid.Col span={1}>
                  <Select
                    leftSection={<IconBrightnessHalf size={15} />}
                    label="Turno"
                    placeholder="Selecione..."
                    data={['1º Turno', '2º Turno', '3º Turno'].map((x, i) => ({
                      value: i.toString(),
                      label: x,
                    }))}
                    searchable
                    clearable
                    {...form.getInputProps('turno')}
                    comboboxProps={{ zIndex: props.zIndex + 1 }}
                  />
                </Grid.Col>
                <Grid.Col span={2}>
                  <TextInput
                    leftSection={<IconClock size={15} />}
                    rightSection={
                      <ActionIcon
                        size="sm"
                        radius="sm"
                        color="primary"
                        style={{ borderColor: theme?.colors?.primary?.[6] }}
                        variant="outline"
                        onClick={() => {
                          form.setFieldValue('jornada', 'Seg-Sex 08:00-17:00,Sab-Sab 08:00-12:00');
                        }}
                      >
                        <IconClock size={15} color={theme?.colors?.primary?.[6]} />
                      </ActionIcon>
                    }
                    label="Jornada"
                    placeholder="Seg-Sex 08:00-17:00,Sab-Sab 08:00-12:00,..."
                    maxLength={100}
                    {...form.getInputProps('jornada')}
                  />
                </Grid.Col>
              </Grid>
              <Space h="xs" />
            </>
          )}

          <SimpleGrid cols={1}>
            <Textarea
              leftSection={<IconNotes size={15} />}
              label="Observação"
              placeholder="Observação sobre o serviço"
              autosize
              maxLength={500}
              {...form.getInputProps('observacao')}
            />
          </SimpleGrid>
        </Paper>
        <Space h="lg" />

        <Paper shadow="xs" p="md" withBorder>
          <PageSection
            size="lg"
            color={Feature.Home.Proposal.color}
            label="Condições Comerciais"
            text="Informações comerciais e financeiras do serviço como preço, imposto e regra de cobrança junto ao cliente."
          />
          <Space h="xs" />
          <BasicPricing
            ref={refBasicPricing}
            isEdit
            disabled={props.disabled}
            zIndex={props.zIndex + 1}
            withFrequency
            validationMode="all-or-none"
            quantityMin={1}
            quantityDefaultValue={1}
            quantityDecimalScale={0}
            quantityStep={1}
            quantityUnitDisabled
            value={{
              ...form.values.precificacao,
            }}
            onChange={(pricingData) => {
              form.setFieldValue('precificacao', pricingData);
            }}
            quantityUnitOptions={props.referenceData.unitOfMeasures
              .filter((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
              .map((x) => ({
                value: x.idUnidadeMedida.toString(),
                label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
                group: x.tipo,
              }))}
            frequencyUnitOptions={props.referenceData.unitOfMeasures
              .filter(
                (x) =>
                  (x.tipo === 'Tempo' &&
                    (x.unidadeMedidaSigla === 'd' ||
                      x.unidadeMedidaSigla === 'sem' ||
                      x.unidadeMedidaSigla === 'mês' ||
                      x.unidadeMedidaSigla === 'ano')) ||
                  (x.tipo === 'Outro' && x.unidadeMedidaSigla === 'per')
              )
              .map((x) => ({
                value: x.idUnidadeMedida.toString(),
                label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
                group: x.tipo,
              }))}
          />
          <Space h="xs" />
          <Switch
            label="Cobrar Tolerâncias?"
            {...form.getInputProps('cobrarTolerancia', { type: 'checkbox' })}
          />
        </Paper>
        <Space h="lg" />

        <Paper shadow="xs" p="md" withBorder>
          <PageSection
            size="lg"
            color={Feature.Home.Proposal.color}
            label="Cliente e Local"
            text="Cliente e local onde a prestação de serviço deverá ocorrer."
          />
          <Space h="xs" />
          <Grid columns={4}>
            <Grid.Col span={2}>
              <EntityPicker
                label="Cliente"
                description={null}
                formItem={form.getInputProps('idCliente') || undefined}
                idsToBeDisabled={[]}
                disabled={props.disabled}
                required
                callback={(item: EntitySearchResponseType | EntitySearchResponseType[] | null) => {
                  if (!Array.isArray(item) && item) {
                    form.setFieldValue('clienteData', item);
                    form.setFieldValue('idCliente', item.idEntidade);
                    form.setFieldValue('cliente', item.nomeFantasia || item.razaoSocial || item.nome || '');
                    form.setFieldValue('idEntidadeEndereco', -1);
                    form.setFieldValue('endereco', '');
                  }
                }}
                zIndex={props.zIndex + 1}
                useCache
                allowMultiple={false}
                query={undefined}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <EntityAddressPicker
                key={form.values.idCliente!}
                label="Endereço"
                idEntidade={form.values.idCliente!}
                formItem={form.getInputProps('idEntidadeEndereco')}
                idsToBeDisabled={[]}
                callback={async (item: EntityAddressType | null) => {
                  if (item) {
                    form.setFieldValue('enderecoData', item);
                    form.setFieldValue('idEntidadeEndereco', item.idEntidadeEndereco!);
                    form.setFieldValue(
                      'endereco',
                      `${item.logradouro}, ${item.numero} | ${item.bairro} - ${item.cidade}/${item.codigoEstado}`
                    );
                  }
                }}
                disabled={props.disabled || !form.values.idCliente}
                required
                zIndex={props.zIndex + 1}
              />
            </Grid.Col>
          </Grid>
        </Paper>
      </form>
    </div>
  );
});

export default ServiceGeneralFormView;
